import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, FormControl, InputLabel, Select, SelectChangeEvent, MenuItem, Card, CardContent, CardActions, IconButton, Divider, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, InputAdornment,
} from '@mui/material';
import {
  ArrowBack as BackIcon, Save as SaveIcon, Cancel as CancelIcon, Add as AddIcon, Remove as RemoveIcon, Person as PersonIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { orderApi, customerApi, stitchItemApi } from '../services/api';
import {
  Order, OrderFormData, OrderItemFormData, MeasurementFormData, OrderApiData, Customer, StitchItem
} from '../types';
import { DEFAULT_MEASUREMENTS, APP_CONFIG } from '../utils/constants';

const OrderEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Data
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stitchItems, setStitchItems] = useState<StitchItem[]>([]);
  const [originalOrder, setOriginalOrder] = useState<Order | null>(null);

  // Form data
  const [formData, setFormData] = useState<OrderFormData>({
    customer_id: 0,
    order_date: new Date().toISOString().split('T')[0], // Current date in YYYY-MM-DD format
    delivery_date: '',
    advance_amount: '0',
    notes: '',
    items: [],
  });

  // UI state
  const [showCustomerDialog, setShowCustomerDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const loadData = async () => {
      // Load initial data first (customers and items)
      await loadInitialData();
      // Then load order so customer can be found in the list
      if (id) {
        await loadOrder(parseInt(id));
      }
    };
    loadData();
  }, [id]);

  const loadOrder = async (orderId: number) => {
    try {
      setLoading(true);
      const response = await orderApi.getById(orderId);
      if (response.status === 'success' && response.data) {
        const order = response.data;
        setOriginalOrder(order);
        setFormData({
          customer_id: order.customer_id,
          order_date: order.order_date || new Date().toISOString().split('T')[0],
          delivery_date: order.delivery_date || '',
          advance_amount: order.advance_amount,
          notes: order.notes || '',
          items: order.items.map(item => ({
            stitch_item_id: item.stitch_item_id,
            quantity: item.quantity,
            unit_price: item.unit_price,
            special_instructions: item.special_instructions || '',
            measurements: item.measurements_data || (() => {
              const itemName = item.stitch_item_name || 'SHIRT';
              const measurements = DEFAULT_MEASUREMENTS[itemName.toUpperCase() as keyof typeof DEFAULT_MEASUREMENTS] || DEFAULT_MEASUREMENTS.SHIRT;
              return measurements.map(name => ({
                measurement_name: name,
                measurement_value: '',
                unit: 'cm',
              }));
            })(),
          })),
        });
      }
    } catch (err) {
      setError('Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    try {
      const [customersResponse, itemsResponse] = await Promise.all([
        customerApi.getAll(),
        stitchItemApi.getAll(),
      ]);

      if (customersResponse.status === 'success') {
        setCustomers(customersResponse.data || []);
      }
      if (itemsResponse.status === 'success') {
        setStitchItems(itemsResponse.data || []);
      }
    } catch (err) {
      setError('Failed to load initial data');
    }
  };

  const handleCustomerSelect = (customer: Customer) => {
    setFormData(prev => ({ ...prev, customer_id: customer.id! }));
    setShowCustomerDialog(false);
  };

  const addOrderItem = (item: StitchItem) => {
    const newOrderItem: OrderItemFormData = {
      stitch_item_id: item.id!,
      quantity: 1,
      unit_price: item.base_price.toString(),
      special_instructions: '',
      measurements: item.measurements?.map(measurement => ({
        measurement_name: measurement.measurement_name,
        measurement_value: '',
        unit: measurement.unit,
      })) || [],
    };
    setFormData(prev => ({ ...prev, items: [...prev.items, newOrderItem] }));
    setShowItemDialog(false);
  };

  const updateOrderItem = (index: number, field: keyof OrderItemFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const removeOrderItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const updateMeasurement = (itemIndex: number, measurementIndex: number, field: keyof MeasurementFormData, value: string) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === itemIndex ? {
          ...item,
          measurements: item.measurements.map((m, j) => 
            j === measurementIndex ? { ...m, [field]: value } : m
          ),
        } : item
      ),
    }));
  };

  const calculateItemTotal = (item: OrderItemFormData) => {
    return (parseFloat(item.unit_price) * item.quantity).toFixed(2);
  };

  const calculateOrderTotal = () => {
    return formData.items.reduce((total, item) => {
      return total + (parseFloat(item.unit_price) * item.quantity);
    }, 0).toFixed(2);
  };

  const calculateBalance = () => {
    const total = parseFloat(calculateOrderTotal());
    const advance = parseFloat(formData.advance_amount);
    return (total - advance).toFixed(2);
  };

  const handleSubmit = async () => {
    if (!formData.customer_id || formData.items.length === 0) {
      setError('Please select a customer and add at least one item');
      return;
    }

    try {
      setSaving(true);
      // Create the order data with proper structure for the API
      // Keep the original status when editing (don't change to pending)
      const orderData: OrderApiData = {
        customer_id: formData.customer_id,
        order_date: formData.order_date,
        delivery_date: formData.delivery_date,
        total_amount: calculateOrderTotal(),
        advance_amount: formData.advance_amount,
        balance_amount: calculateBalance(),
        status: originalOrder?.status || 'pending', // Keep original status when editing
        notes: formData.notes,
        items: formData.items.map(item => ({
          stitch_item_id: item.stitch_item_id,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: calculateItemTotal(item),
          measurements_data: item.measurements,
          special_instructions: item.special_instructions || ''
        }))
      };

      const response = await orderApi.update(parseInt(id!), orderData);

      if (response.status === 'success') {
        navigate(`/orders/${id}`);
      } else {
        setError(response.message || 'Failed to update order');
      }
    } catch (err) {
      setError('Failed to update order');
    } finally {
      setSaving(false);
    }
  };

  const getCustomerName = (customerId: number) => {
    // First check if we have customer in the customers list
    const customer = customers.find(c => c.id === customerId);
    if (customer) {
      return customer.name;
    }
    // If not found in list, check if order has customer info
    if (originalOrder?.customer_name) {
      return originalOrder.customer_name;
    }
    // If still not found, check if order has customer object
    if (originalOrder?.customer?.name) {
      return originalOrder.customer.name;
    }
    // Default fallback
    return customerId > 0 ? `Customer #${customerId}` : 'Select Customer';
  };

  const getItemName = (itemId: number) => {
    const item = stitchItems.find(i => i.id === itemId);
    return item ? item.name : 'Unknown Item';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(`/orders/${id}`)}
          sx={{ mr: 2 }}
        >
          Back to Order
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          {/* Customer Selection */}
          <Box sx={{ flex: '1 1 400px', minWidth: '350px' }}>
            <Typography variant="h6" gutterBottom>
              Customer
            </Typography>
            <TextField
              fullWidth
              label="Customer"
              value={getCustomerName(formData.customer_id)}
              InputProps={{
                readOnly: true,
                startAdornment: <PersonIcon sx={{ mr: 1 }} />,
              }}
              onClick={() => setShowCustomerDialog(true)}
              sx={{ cursor: 'pointer' }}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowCustomerDialog(true)}
              sx={{ mt: 1 }}
            >
              Change Customer
            </Button>
          </Box>

          {/* Delivery Date */}
          <Box sx={{ flex: '1 1 400px', minWidth: '350px' }}>
            <Typography variant="h6" gutterBottom>
              Delivery Date
            </Typography>
            <TextField
              fullWidth
              type="date"
              label="Delivery Date"
              value={formData.delivery_date}
              onChange={(e) => setFormData(prev => ({ ...prev, delivery_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
            />
          </Box>

          {/* Order Items */}
          <Box sx={{ flex: '1 1 100%' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Order Items</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setShowItemDialog(true)}
              >
                Add Item
              </Button>
            </Box>

            {formData.items.map((item, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
                      <Typography variant="subtitle2" color="primary">
                        {getItemName(item.stitch_item_id)}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateOrderItem(index, 'quantity', parseInt(e.target.value) || 1)}
                        inputProps={{ min: 1 }}
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                      <TextField
                        fullWidth
                        label="Unit Price"
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => updateOrderItem(index, 'unit_price', e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{APP_CONFIG.CURRENCY}</InputAdornment>,
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
                      <Typography variant="subtitle2">
                        Total: {APP_CONFIG.CURRENCY}{calculateItemTotal(item)}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                      <IconButton
                        color="error"
                        onClick={() => removeOrderItem(index)}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Measurements */}
                  {item.measurements && item.measurements.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="primary" gutterBottom>
                        Measurements
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                        {item.measurements.map((measurement, measurementIndex) => (
                          <Box sx={{ flex: '1 1 200px', minWidth: '150px' }} key={measurementIndex}>
                            <TextField
                              fullWidth
                              label={measurement.measurement_name}
                              value={measurement.measurement_value}
                              onChange={(e) => updateMeasurement(index, measurementIndex, 'measurement_value', e.target.value)}
                              InputProps={{
                                endAdornment: <InputAdornment position="end">{measurement.unit}</InputAdornment>,
                              }}
                            />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* Special Instructions */}
                  <TextField
                    fullWidth
                    label="Special Instructions"
                    value={item.special_instructions}
                    onChange={(e) => updateOrderItem(index, 'special_instructions', e.target.value)}
                    multiline
                    rows={2}
                    sx={{ mt: 2 }}
                  />
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Payment Details */}
          <Box sx={{ flex: '1 1 400px', minWidth: '350px' }}>
            <Typography variant="h6" gutterBottom>
              Payment Details
            </Typography>
            <TextField
              fullWidth
              label="Advance Amount"
              type="number"
              value={formData.advance_amount}
              onChange={(e) => setFormData(prev => ({ ...prev, advance_amount: e.target.value }))}
              InputProps={{
                startAdornment: <InputAdornment position="start">{APP_CONFIG.CURRENCY}</InputAdornment>,
              }}
              sx={{ mb: 2 }}
            />
            
            <Card>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>
                  Payment Summary
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Total Amount:</Typography>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {APP_CONFIG.CURRENCY}{calculateOrderTotal()}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography>Advance Paid:</Typography>
                  <Typography variant="subtitle1" color="success.main">
                    {APP_CONFIG.CURRENCY}{formData.advance_amount}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle1" fontWeight="bold">
                    Balance Due:
                  </Typography>
                  <Typography variant="subtitle1" color="error.main" fontWeight="bold">
                    {APP_CONFIG.CURRENCY}{calculateBalance()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Notes */}
          <Box sx={{ flex: '1 1 400px', minWidth: '350px' }}>
            <Typography variant="h6" gutterBottom>
              Notes
            </Typography>
            <TextField
              fullWidth
              label="Order Notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              multiline
              rows={8}
            />
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={() => navigate(`/orders/${id}`)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
          >
            {saving ? 'Updating...' : 'Update Order'}
          </Button>
        </Box>
      </Paper>

      {/* Customer Selection Dialog */}
      <Dialog 
        open={showCustomerDialog} 
        onClose={() => setShowCustomerDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Customer</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
          />
          <List>
            {customers
              .filter(customer => 
                customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                customer.phone.includes(searchTerm) ||
                (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase()))
              )
              .map(customer => (
                <ListItem 
                  key={customer.id}
                  onClick={() => handleCustomerSelect(customer)}
                  sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                >
                  <ListItemText
                    primary={customer.name}
                    secondary={`${customer.phone}${customer.email ? ` • ${customer.email}` : ''}`}
                  />
                </ListItem>
              ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomerDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Item Selection Dialog */}
      <Dialog 
        open={showItemDialog} 
        onClose={() => setShowItemDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Select Stitch Item</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {stitchItems.map(item => (
              <Box sx={{ flex: '1 1 400px', minWidth: '350px' }} key={item.id}>
                <Card>
                  <CardContent>
                    <Typography variant="h6">{item.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item.description}
                    </Typography>
                    <Typography variant="subtitle1" color="primary" sx={{ mt: 1 }}>
                      Base Price: {APP_CONFIG.CURRENCY}{item.base_price}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button
                      variant="contained"
                      onClick={() => addOrderItem(item)}
                      fullWidth
                    >
                      Add to Order
                    </Button>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowItemDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderEdit; 